import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { Bot, Building2, Clock, LifeBuoy, MessageSquarePlus, Phone, RotateCcw, Search, UserRoundCheck } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useFocus } from '../../app/focus';
import { useUser } from '../../app/session';
import { InsightBadges, InsightView, useActiveInsights, useInsightsFor } from '../../components/InsightBadges';
import { TeamBadge, teamColor } from '../../components/TeamBadge';
import { Badge, Button, Empty, Input, Spinner } from '../../components/ui';
import { get, hasPermission } from '../../lib/api';
import { contactLabel, formatDuration, shortTime, since, statusLabel } from '../../lib/format';
import { ListView, useAttendances, ticketLabel } from '../../lib/queries';
import { AttendanceItem, WaitingCall } from '../../lib/types';
import { WaitingCalls } from './WaitingCalls';
import { typingLabel, useTyping } from '../../app/typing';

const TABS: Array<{ view: ListView; label: string; readAll?: boolean }> = [
  { view: 'queue', label: 'Fila' },
  { view: 'mine', label: 'Meus' },
  // Sem supervisão: atendimentos de outros operadores, para entrar e ajudar.
  { view: 'helpable', label: 'Ajudar', readAll: false },
  { view: 'active', label: 'Todos', readAll: true },
  { view: 'finished', label: 'Finalizados', readAll: true },
];

const PERIODS = [
  { days: 1, label: 'Hoje', hint: 'hoje' },
  { days: 7, label: '7 dias', hint: 'nos últimos 7 dias' },
  { days: 30, label: '30 dias', hint: 'nos últimos 30 dias' },
  { days: 0, label: 'Tudo', hint: 'no histórico' },
];

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/** Re-renderiza a cada 30 s (tempo na fila). */
function useTick(ms = 30_000) {
  const [, set] = useState(0);
  useEffect(() => {
    const t = setInterval(() => set((n) => n + 1), ms);
    return () => clearInterval(t);
  }, [ms]);
}

export function AttendanceList({
  selectedId,
  onSelect,
  onTake,
  onStartConversation,
}: {
  selectedId: string | null;
  onSelect: (id: string) => void;
  onTake: (id: string) => void;
  onStartConversation?: () => void;
}) {
  const user = useUser();
  const readAll = hasPermission(user, 'attendance:read_all');
  const [view, setView] = useState<ListView>('mine');
  const [filter, setFilter] = useState('');
  const [days, setDays] = useState(1);
  // Todos/Finalizados buscam no servidor (o histórico pode ser grande); Fila/Meus filtram na tela.
  const serverSide = view === 'finished' || view === 'active';
  const serverQuery = useDebounced(serverSide ? filter : '', 350);
  const queue = useAttendances('queue');
  const mine = useAttendances('mine');
  // Ajudar vem do servidor (atendimentos dos colegas) e filtra na tela.
  const current = useAttendances(view, serverSide || view === 'helpable', { days, q: serverQuery });
  const list = view === 'queue' ? queue : view === 'mine' ? mine : current;
  // Ligações esperando na fila do PBX: destaque na aba Fila (mesma consulta do bloco da fila).
  const insights = useActiveInsights();
  const waitingCalls = useQuery({ queryKey: ['calls', 'waiting'], queryFn: () => get<WaitingCall[]>('/telephony/queue/waiting'), refetchInterval: 30_000 }).data?.length ?? 0;
  useTick();

  const items = useMemo(() => {
    const q = filter.trim().toLowerCase();
    let all = list.data ?? [];
    // Fila: urgentes e clientes irritados primeiro (análise da IA); o resto na ordem de chegada.
    if (view === 'queue' && insights.size) {
      const weight = (id: string) => {
        const i = insights.get(id);
        return i ? (i.urgency === 'alta' ? 2 : i.urgency === 'media' ? 1 : 0) + (i.mood === 'irritado' ? 2 : i.mood === 'impaciente' ? 1 : 0) : 0;
      };
      all = [...all].sort((a, b) => weight(b.id) - weight(a.id));
    }
    if (!q || serverSide) return all;
    return all.filter((a) => [a.contactName, a.contactPhone, a.customerName, a.ticketId, a.lastMessagePreview].some((v) => v?.toLowerCase().includes(q)));
  }, [list.data, filter, serverSide, view, insights]);
  // Finalizados: a última análise da IA continua no atendimento (urgência e se o cliente ficou irritado).
  const finishedInsights = useInsightsFor(view === 'finished' ? items.slice(0, 200).map((a) => a.id) : []);

  const counts: Partial<Record<ListView, number>> = { queue: queue.data?.length, mine: mine.data?.length };

  return (
    <aside className="flex w-full min-w-0 flex-col border-r border-border bg-panel">
      {onStartConversation && (
        <div className="border-b border-border p-2">
          <Button variant="primary" className="w-full" onClick={onStartConversation} title="Iniciar conversa com um cliente cadastrado">
            <MessageSquarePlus className="size-4" /> Nova conversa
          </Button>
        </div>
      )}
      <div className="flex gap-1 border-b border-border p-2">
        {TABS.filter((t) => t.readAll === undefined || t.readAll === readAll).map((t) => (
          <button
            key={t.view}
            onClick={() => setView(t.view)}
            className={clsx(
              'flex flex-1 items-center justify-center gap-1 rounded-md px-2 py-1.5 text-xs font-medium',
              view === t.view ? 'bg-accent text-accent-fg' : 'text-fg-soft hover:bg-muted',
              t.view === 'queue' && waitingCalls > 0 && view !== 'queue' && 'ring-2 ring-warn',
            )}
            title={t.view === 'queue' && waitingCalls > 0 ? `${waitingCalls} ligação(ões) esperando na fila` : undefined}
          >
            {t.label}
            {counts[t.view] ? <span className="opacity-80">({counts[t.view]})</span> : null}
            {t.view === 'queue' && waitingCalls > 0 && (
              <span className="inline-flex animate-pulse items-center gap-0.5 rounded-full bg-warn px-1.5 py-0.5 text-[10px] font-bold text-white">
                <Phone className="size-3" />
                {waitingCalls}
              </span>
            )}
          </button>
        ))}
      </div>
      <div className="border-b border-border p-2">
        {/* A lupa fica presa ao campo (na aba Finalizados há a linha dos períodos embaixo). */}
        <div className="relative">
          <Search className="pointer-events-none absolute top-1/2 left-2 size-3.5 -translate-y-1/2 text-fg-soft" />
          <Input
            placeholder={view === 'finished' ? 'Buscar: nome, telefone, cliente, chamado, resumo' : 'Buscar por nome, telefone, cliente, chamado'}
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="h-8 pl-7 text-xs"
          />
        </div>
        {view === 'finished' && (
          <div className="mt-2 flex gap-1">
            {PERIODS.map((p) => (
              <button
                key={p.days}
                onClick={() => setDays(p.days)}
                className={clsx('flex-1 rounded px-1.5 py-1 text-[11px]', days === p.days ? 'bg-muted font-medium text-fg' : 'text-fg-soft hover:bg-muted')}
              >
                {p.label}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {view === 'queue' && <WaitingCalls />}
        {list.isLoading ? (
          <Empty>
            <Spinner />
          </Empty>
        ) : items.length === 0 ? (
          <Empty>
            {view === 'queue'
              ? 'Fila vazia.'
              : view === 'mine'
                ? 'Nenhum atendimento com você.'
                : view === 'helpable'
                  ? 'Nenhum atendimento em andamento com outros operadores.'
                  : view === 'finished'
                  ? `Nenhum atendimento finalizado ${PERIODS.find((p) => p.days === days)?.hint}.`
                  : 'Nada por aqui.'}
          </Empty>
        ) : (
          items.map((a) => <Row key={a.id} a={a} insight={insights.get(a.id) ?? finishedInsights.get(a.id)} selected={a.id === selectedId} userId={user.id} onSelect={onSelect} onTake={onTake} />)
        )}
      </div>
    </aside>
  );
}

function Row({
  a,
  insight,
  selected,
  userId,
  onSelect,
  onTake,
}: {
  a: AttendanceItem;
  insight: InsightView | undefined;
  selected: boolean; userId: string; onSelect: (id: string) => void; onTake: (id: string) => void }) {
  const { focus } = useFocus();
  const focused = focus?.attendanceId === a.id;
  const typing = useTyping(a.status === 'finalizado' ? null : a.conversationId);
  // "novo" = cliente ainda no menu de equipes: dá para assumir antes da escolha.
  const takeable = a.status === 'na_fila' || a.status === 'novo' || (a.status === 'transferido' && a.operatorId === userId);
  return (
    <div
      onClick={() => onSelect(a.id)}
      className={clsx('cursor-pointer border-b border-l-4 border-border px-3 py-2.5', selected ? 'bg-accent/10' : 'hover:bg-muted')}
      style={{ borderLeftColor: focused ? 'var(--ok)' : a.teamName ? teamColor(a.teamName) : 'transparent' }}
      title={a.teamName ? `Equipe ${a.teamName}` : undefined}
    >
      <div className="flex items-center gap-2">
        <span className="min-w-0 flex-1 truncate font-medium">{contactLabel(a)}</span>
        <span className="shrink-0 text-[11px] text-fg-soft">{shortTime(a.status === 'finalizado' ? a.finishedAt : (a.lastMessageAt ?? a.queuedAt))}</span>
      </div>
      <div className="mt-0.5 flex items-center gap-2 text-xs text-fg-soft">
        {typing ? (
          <span className="min-w-0 flex-1 truncate font-medium text-accent">{typingLabel(typing)}</span>
        ) : (
          <span className="min-w-0 flex-1 truncate">{a.lastMessagePreview ?? '—'}</span>
        )}
        {a.unreadCount > 0 && <span className="rounded-full bg-accent px-1.5 text-[10px] font-semibold text-accent-fg">{a.unreadCount}</span>}
      </div>
      <div className="mt-1.5 flex flex-wrap items-center gap-1">
        {a.customerName ? (
          <Badge tone="accent" className="text-xs font-semibold">
            <Building2 className="size-3" /> {a.customerName}
          </Badge>
        ) : (
          <Badge tone="warn">sem cliente</Badge>
        )}
        <TeamBadge name={a.teamName} />
        <InsightBadges insight={insight} />
        {a.channelType === 'voice' && (
          <Badge>
            <Phone className="size-3" /> ligação
          </Badge>
        )}
        {a.status === 'novo' && (
          <Badge tone="warn">
            <Bot className="size-3" /> escolhendo equipe
          </Badge>
        )}
        {a.returning && (
          <Badge tone="accent">
            <RotateCcw className="size-3" /> retorno
          </Badge>
        )}
        {a.status === 'transferido' && <Badge tone="accent">{a.operatorId === userId ? 'transferido para você' : `transferido: ${a.operatorName ?? ''}`}</Badge>}
        {a.status === 'na_fila' && (
          <Badge>
            <Clock className="size-3" /> {since(a.queuedAt)}
          </Badge>
        )}
        {a.status === 'em_atendimento' && a.operatorId !== userId && <Badge>{a.operatorName}</Badge>}
        {a.participants?.some((p) => p.userId === userId) ? (
          <Badge tone="warn">
            <LifeBuoy className="size-3" /> você está ajudando
          </Badge>
        ) : a.participants?.length > 0 ? (
          <Badge>
            <LifeBuoy className="size-3" /> +{a.participants.length} ajudando
          </Badge>
        ) : null}
        {a.ticketId && <Badge>{ticketLabel(a)}</Badge>}
        {a.status === 'finalizado' && <Badge tone={a.finishReason === 'cancelled' ? 'warn' : 'neutral'}>{statusLabel(a)}</Badge>}
        {focused && <Badge tone="ok">na tela</Badge>}
        {!focused && a.status === 'em_atendimento' && a.operatorId === userId && a.totalSeconds > 0 && <Badge>{formatDuration(a.totalSeconds)}</Badge>}
        {takeable && (
          <Button
            size="sm"
            variant="primary"
            className="ml-auto h-6"
            onClick={(e) => {
              e.stopPropagation();
              onSelect(a.id);
              onTake(a.id);
            }}
          >
            <UserRoundCheck className="size-3.5" /> Assumir
          </Button>
        )}
      </div>
    </div>
  );
}
